/**
 * Resolve maritime heading (degrees, 0 = North, 90 = East, clockwise) from a vessel row.
 * Prefers track-derived bearing when present (playback / interpolated states).
 */
export function resolveVesselMaritimeHeadingDeg(obj) {
  if (!obj) return null;
  const raw =
    obj.movementBearing ??
    obj.message_bearing ??
    obj.heading_deg ??
    obj.direction ??
    obj.heading ??
    obj.message_course_over_ground ??
    obj.course_over_ground ??
    obj.cog;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

/**
 * Compass bearing (0 = North, 90 = East, clockwise) that the vessel bow points when
 * deck.gl IconLayer getAngle is 0. Measured the same way as maritime heading.
 * The velocity-arrow SVG in MapContainer uses B = 0 (triangle points north at 0°).
 * Raster vessel art is not drawn bow-north at 0° — values below match each PNG.
 */
export const VESSEL_ICON_BOW_COMPASS_AT_ANGLE_ZERO = {
  friendly: 225,
  threat: 225,
  defensive: 270,
  auv: 90
};

/**
 * deck.gl IconLayer getAngle (degrees, CCW) so the bow points along heading h (maritime degrees).
 * With billboard:false, deck applies rotation R such that a sprite whose bow sits at compass B when R=0
 * ends with bow at h: (B - R) ≡ h (mod 360) → R = (B - h + 360) % 360.
 * When B = 0 this matches `vessel-velocity-arrow-heads` in MapContainer: (360 - h) % 360.
 */
export function deckIconAngleFromMaritimeHeadingDeg(headingDeg, bowCompassAtAngleZeroDeg = 0) {
  const h = ((Number(headingDeg) % 360) + 360) % 360;
  const B = ((Number(bowCompassAtAngleZeroDeg) % 360) + 360) % 360;
  return (B - h + 360) % 360;
}

/**
 * @param {object} obj - Vessel row
 * @param {number} [bowCompassAtAngleZeroDeg=0] - See {@link VESSEL_ICON_BOW_COMPASS_AT_ANGLE_ZERO}
 */
export function vesselRowToDeckIconAngle(obj, bowCompassAtAngleZeroDeg = 0) {
  const h = resolveVesselMaritimeHeadingDeg(obj);
  if (h == null) return 0;
  return deckIconAngleFromMaritimeHeadingDeg(h, bowCompassAtAngleZeroDeg);
}
